/**
 * Dashv2 main.ts §6 profileColumns + §7 auditDataQuality 의 isomorphic 포팅.
 * server (저장된 dashboard 의 GET /profile) 와 client (chat artifact 의 분석 버튼) 가 같은 코드.
 *
 * 입력은 row-as-object 배열. server 는 saved_query 실행 결과를 row-as-object 로 변환 후 호출,
 * client 는 artifact RPC 의 응답 (`{columns, rows: [{col: val}]}`) 을 그대로 호출.
 */
const MISSING_WARN_PCT = 30;
const LOW_CARD_ABS = 10;
const MED_CARD_ABS = 50;
function isMissing(v) {
    return v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
}
function asNumber(v) {
    if (typeof v === 'number')
        return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
        const n = Number(v.replace(/,/g, ''));
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
function asDate(v) {
    if (v instanceof Date)
        return Number.isNaN(v.getTime()) ? null : v;
    if (typeof v === 'string' || typeof v === 'number') {
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
}
function inferDtype(values) {
    const sample = values.filter((v) => !isMissing(v)).slice(0, 200);
    if (!sample.length)
        return 'mixed';
    let nums = 0;
    let dates = 0;
    let bools = 0;
    for (const v of sample) {
        if (typeof v === 'boolean') {
            bools++;
            continue;
        }
        if (v instanceof Date) {
            dates++;
            continue;
        }
        if (asNumber(v) !== null) {
            nums++;
            continue;
        }
        if (typeof v === 'string' && asDate(v) !== null && /\d{4}-\d{2}-\d{2}/.test(v)) {
            dates++;
        }
    }
    const t = sample.length;
    if (bools / t > 0.9)
        return 'boolean';
    if (dates / t > 0.8)
        return 'datetime';
    if (nums / t > 0.8)
        return 'numeric';
    return 'categorical';
}
function classifyRole(dtype, nUnique, values) {
    if (dtype === 'datetime')
        return 'time_dimension';
    if (dtype === 'boolean')
        return 'filter_dimension';
    if (dtype === 'categorical') {
        if (nUnique <= LOW_CARD_ABS)
            return 'filter_dimension';
        if (nUnique <= MED_CARD_ABS)
            return 'breakdown_dimension';
        return 'identifier';
    }
    if (dtype === 'numeric') {
        const nums = values.map(asNumber).filter((n) => n !== null);
        if (nums.length >= 3) {
            let monotonic = true;
            for (let i = 1; i < nums.length; i++) {
                const prev = nums[i - 1];
                const cur = nums[i];
                if (prev !== undefined && cur !== undefined && cur <= prev) {
                    monotonic = false;
                    break;
                }
            }
            if (monotonic && new Set(nums).size === nums.length)
                return 'identifier';
        }
        return 'measure';
    }
    return 'identifier';
}
function round4(n) {
    return Math.round(n * 10000) / 10000;
}
function summarizeColumn(name, values) {
    const dtype = inferDtype(values);
    const nonMissing = values.filter((v) => !isMissing(v));
    const nUnique = new Set(nonMissing.map((v) => (v instanceof Date ? v.getTime() : v))).size;
    const missingPct = Math.round(((values.length - nonMissing.length) / Math.max(values.length, 1)) * 1000) / 10;
    const role = classifyRole(dtype, nUnique, nonMissing);
    const out = {
        name,
        dtype,
        n_unique: nUnique,
        missing_pct: missingPct,
        role_candidate: role,
    };
    if (missingPct >= MISSING_WARN_PCT)
        out.warning = `결측률 ${missingPct}%`;
    if (dtype === 'numeric') {
        const nums = nonMissing.map(asNumber).filter((n) => n !== null);
        if (nums.length) {
            out.min = round4(Math.min(...nums));
            out.max = round4(Math.max(...nums));
            out.mean = round4(nums.reduce((a, b) => a + b, 0) / nums.length);
            const sorted = [...nums].sort((a, b) => a - b);
            out.median = round4(sorted[Math.floor(sorted.length / 2)] ?? 0);
        }
    }
    else if (dtype === 'datetime') {
        const dates = nonMissing.map(asDate).filter((d) => d !== null);
        if (dates.length) {
            const mn = Math.min(...dates.map((d) => d.getTime()));
            const mx = Math.max(...dates.map((d) => d.getTime()));
            out.min = new Date(mn).toISOString().slice(0, 10);
            out.max = new Date(mx).toISOString().slice(0, 10);
        }
    }
    else if (dtype === 'categorical') {
        const counts = {};
        for (const v of nonMissing) {
            const k = String(v);
            counts[k] = (counts[k] ?? 0) + 1;
        }
        const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
        out.top_values = sorted.slice(0, 10).map(([value, count]) => ({
            value,
            count,
            pct: round4((count / nonMissing.length) * 100),
        }));
    }
    return out;
}
export function profileColumns(rows) {
    if (!rows.length)
        return [];
    const first = rows[0];
    if (!first)
        return [];
    return Object.keys(first).map((name) => summarizeColumn(name, rows.map((r) => r[name])));
}
export function auditDataQuality(rows, columns) {
    const issues = [];
    let missingTotal = 0;
    let missingCells = 0;
    for (const c of columns) {
        missingTotal += rows.length;
        missingCells += Math.round((rows.length * c.missing_pct) / 100);
        if (c.missing_pct >= 60) {
            issues.push({
                severity: 'error',
                column: c.name,
                message: `결측률 ${c.missing_pct}% — 분석 정확도에 큰 영향`,
                hint: '결측 행 제외 또는 컬럼 제외 권장',
            });
        }
        else if (c.missing_pct >= 30) {
            issues.push({
                severity: 'warn',
                column: c.name,
                message: `결측률 ${c.missing_pct}% — 일부 분석에 영향 가능`,
                hint: '필터 또는 imputation 고려',
            });
        }
        else if (c.missing_pct >= 10) {
            issues.push({
                severity: 'info',
                column: c.name,
                message: `결측률 ${c.missing_pct}%`,
                hint: '대부분 분석에 무관',
            });
        }
    }
    const avgMissing = missingTotal > 0 ? (missingCells / missingTotal) * 100 : 0;
    const seen = new Set();
    let dupCount = 0;
    for (const r of rows) {
        const key = Object.values(r)
            .map((v) => (v instanceof Date ? v.toISOString() : String(v ?? '')))
            .join('|');
        if (seen.has(key))
            dupCount++;
        else
            seen.add(key);
    }
    if (dupCount > 0) {
        const dupPct = (dupCount / rows.length) * 100;
        if (dupPct >= 5) {
            issues.push({
                severity: 'warn',
                message: `중복 행 ${dupCount.toLocaleString('ko')}개 (${dupPct.toFixed(1)}%) 발견`,
                hint: '집계가 부풀려질 수 있음. 제거 권장',
            });
        }
        else {
            issues.push({
                severity: 'info',
                message: `중복 행 ${dupCount.toLocaleString('ko')}개 (${dupPct.toFixed(1)}%)`,
                hint: '소량 — 일반적으로 무시 가능',
            });
        }
    }
    let timeGapDetected = false;
    const timeCol = columns.find((c) => c.role_candidate === 'time_dimension');
    if (timeCol) {
        const dates = rows
            .map((r) => asDate(r[timeCol.name]))
            .filter((d) => d !== null)
            .sort((a, b) => a.getTime() - b.getTime());
        if (dates.length >= 4) {
            const gaps = [];
            for (let i = 1; i < dates.length; i++) {
                const a = dates[i - 1]?.getTime();
                const b = dates[i]?.getTime();
                if (a !== undefined && b !== undefined)
                    gaps.push(b - a);
            }
            const sortedGaps = [...gaps].sort((a, b) => a - b);
            const median = sortedGaps[Math.floor(sortedGaps.length / 2)] ?? 0;
            const bigGaps = gaps.filter((g) => g > median * 5 && g > 86400000 * 7);
            if (bigGaps.length) {
                timeGapDetected = true;
                const maxGapDays = Math.round(Math.max(...bigGaps) / 86400000);
                issues.push({
                    severity: 'info',
                    column: timeCol.name,
                    message: `시계열에 큰 갭 ${bigGaps.length}건 (최대 ${maxGapDays}일)`,
                    hint: '특정 기간 데이터 누락 가능 — 시계열 분석 시 주의',
                });
            }
        }
    }
    if (rows.length < 10) {
        issues.push({
            severity: 'error',
            message: `데이터 행 ${rows.length}개 — 통계적 분석에 부족`,
            hint: '최소 30행 이상 권장',
        });
    }
    else if (rows.length < 30) {
        issues.push({
            severity: 'warn',
            message: `데이터 행 ${rows.length}개 — 표본이 작음`,
            hint: '결과 해석에 신중',
        });
    }
    if (!timeCol) {
        issues.push({
            severity: 'info',
            message: '시간 컬럼이 감지되지 않음',
            hint: '시계열 분석·MoM/YoY 비교 불가',
        });
    }
    let score = 100;
    score -= Math.min(40, avgMissing * 1.0);
    score -= Math.min(20, rows.length > 0 ? (dupCount / rows.length) * 200 : 0);
    score -= issues.filter((i) => i.severity === 'error').length * 8;
    score -= issues.filter((i) => i.severity === 'warn').length * 3;
    score = Math.max(0, Math.round(score));
    const grade = score >= 90 ? 'A' : score >= 75 ? 'B' : score >= 60 ? 'C' : 'D';
    return {
        score,
        grade,
        metrics: {
            rows: rows.length,
            columns: columns.length,
            avgMissing: Math.round(avgMissing * 10) / 10,
            duplicates: dupCount,
            hasTimeCol: !!timeCol,
            timeGapDetected,
        },
        issues,
    };
}
//# sourceMappingURL=profile.js.map